/**
 * Schémas Zod — Retention / Legal Hold / Sensitive Data (lecture seule en 9.H.2).
 *
 * Source DB : migration 0024 (`docs/design/retention-and-sensitive-data.md`).
 *
 * Pas de Create/Update/Delete en 9.H.2 :
 *   • Les écritures (créer policy, placer/relâcher legal_hold, etc.) viendront
 *     en 9.H.3 avec D11 human-gating dédié.
 *   • Le scope `retention:write` n'existe pas en 9.H.2.
 *
 * Notes décisions PO :
 *   • PO-Q9 Option C : `retention:read` est ajouté à l'enum DB + au miroir TS
 *     mais N'EST PAS inclus dans le bundle `mcp:read` (cf. api-keys.ts inchangé).
 *   • PO-Q4 : `is_legal_hold_active(scope_kind, scope_id?)` retourne `false`
 *     si la ressource n'existe pas — il NE PROUVE PAS l'existence de la
 *     ressource. Documenté dans la description du tool MCP correspondant.
 *   • PO-Q3 : listing `retention_audit_log` exige au moins UN filtre OU une
 *     fenêtre temporelle (from / to) — pattern 9.F.1 F-01.
 */

import { z } from "zod";

import { PaginationSchema, UuidSchema } from "./common.js";

// =============================================================================
// Enums miroir DB (ADR 0024)
// =============================================================================

export const DataClassSchema = z.enum([
  "public",
  "internal",
  "pii",
  "sensitive_health",
  "sensitive_legal",
  "sensitive_financial",
  "secret",
]);
export type DataClass = z.infer<typeof DataClassSchema>;

export const ScrubbingStrategySchema = z.enum(["redact", "hash", "anonymize", "preserve"]);
export type ScrubbingStrategy = z.infer<typeof ScrubbingStrategySchema>;

export const RetentionActionSchema = z.enum(["anonymize", "archive", "purge"]);
export type RetentionAction = z.infer<typeof RetentionActionSchema>;

export const RetentionPolicyBasisSchema = z.enum([
  "amf",
  "loi25",
  "pipeda",
  "fiscal_cra",
  "security",
  "operational",
  "tenant_choice",
]);
export type RetentionPolicyBasis = z.infer<typeof RetentionPolicyBasisSchema>;

export const LegalHoldScopeKindSchema = z.enum([
  "tenant",
  "company",
  "project",
  "submission",
  "quote_request",
  "intake_event",
  "document_link",
  "employee_census",
]);
export type LegalHoldScopeKind = z.infer<typeof LegalHoldScopeKindSchema>;

export const LegalHoldStatusSchema = z.enum(["active", "released", "expired"]);
export type LegalHoldStatus = z.infer<typeof LegalHoldStatusSchema>;

export const RetentionActionLogSchema = z.enum([
  "policy_created",
  "policy_updated",
  "policy_deactivated",
  "anonymized",
  "archived",
  "purged",
  "export_generated",
  "legal_hold_placed",
  "legal_hold_released",
  // Ajoutées 9.H.4 (migration 0026) — worker data-retention dry-run.
  "dry_run_planned",
  "dry_run_no_policy",
]);
export type RetentionActionLog = z.infer<typeof RetentionActionLogSchema>;

// =============================================================================
// Row schemas — lecture seule
// =============================================================================

/**
 * `app.data_class_registry` — clé composite (table_name, column_name).
 * Pas d'`id`, pas de `tenant_id` (référentiel global).
 */
export const DataClassRegistryRowSchema = z.object({
  table_name: z.string(),
  column_name: z.string(),
  data_class: DataClassSchema,
  scrubbing_strategy: ScrubbingStrategySchema,
  notes: z.string().nullable(),
  created_at: z.string(),
});
export type DataClassRegistryRow = z.infer<typeof DataClassRegistryRowSchema>;

/**
 * `app.retention_policies`. `tenant_id` peut être NULL (policy globale par
 * défaut). Pas de `deleted_at` — désactivation via `active = false`.
 */
export const RetentionPolicyRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema.nullable(),
  data_category: z.string(),
  module: z.string().nullable(),
  retention_days: z.number().int().nullable(),
  legal_minimum_days: z.number().int().nullable(),
  policy_basis: RetentionPolicyBasisSchema.nullable(),
  action_on_expiry: RetentionActionSchema,
  active: z.boolean(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type RetentionPolicyRow = z.infer<typeof RetentionPolicyRowSchema>;

/**
 * `app.legal_holds`. `scope_id` NULL ssi `scope_kind = 'tenant'` (CHECK DB).
 * Pas de `deleted_at` — release via `status = 'released'` + `released_at`.
 */
export const LegalHoldRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  scope_kind: LegalHoldScopeKindSchema,
  scope_id: UuidSchema.nullable(),
  reason: z.string(),
  legal_reference: z.string().nullable(),
  status: LegalHoldStatusSchema,
  placed_by: UuidSchema,
  placed_at: z.string(),
  released_by: UuidSchema.nullable(),
  released_at: z.string().nullable(),
  release_reason: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type LegalHoldRow = z.infer<typeof LegalHoldRowSchema>;

/**
 * `app.retention_audit_log`. Pas de `tenant_id` NULL — toujours scoped.
 * Append-only ; pas d'`updated_at`.
 */
export const RetentionAuditLogRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  action: RetentionActionLogSchema,
  target_kind: z.string().nullable(),
  target_id: UuidSchema.nullable(),
  policy_id: UuidSchema.nullable(),
  legal_hold_id: UuidSchema.nullable(),
  acted_by_user: UuidSchema.nullable(),
  acted_by_agent: UuidSchema.nullable(),
  metadata: z.record(z.unknown()).nullable(),
  created_at: z.string(),
});
export type RetentionAuditLogRow = z.infer<typeof RetentionAuditLogRowSchema>;

// =============================================================================
// Filtres listing
// =============================================================================

/**
 * GET /v1/data-class-registry — filtres optionnels (référentiel modeste,
 * pas de contrainte "au moins un filtre").
 */
export const DataClassRegistryFilterSchema = z.object({
  table_name: z.string().min(1).max(120).optional(),
  column_name: z.string().min(1).max(120).optional(),
  data_class: DataClassSchema.optional(),
});
export type DataClassRegistryFilter = z.infer<typeof DataClassRegistryFilterSchema>;

/**
 * GET /v1/retention-policies — filtres optionnels.
 *
 * `include_global` (défaut `true`) : si `true`, inclut les policies globales
 * (`tenant_id IS NULL`) en plus de celles du tenant courant. Quand `false`,
 * ne renvoie QUE les overrides du tenant courant.
 */
export const RetentionPolicyFilterSchema = z.object({
  data_category: z.string().min(1).max(120).optional(),
  module: z.string().min(1).max(120).optional(),
  active: z.boolean().optional(),
  policy_basis: RetentionPolicyBasisSchema.optional(),
  include_global: z.boolean().optional(),
});
export type RetentionPolicyFilter = z.infer<typeof RetentionPolicyFilterSchema>;

/**
 * GET /v1/retention-policies/effective — query strict.
 * Renvoie 1 policy ou null (pas de paginated list).
 */
export const RetentionPolicyEffectiveQuerySchema = z.object({
  data_category: z.string().min(1).max(120),
  module: z.string().min(1).max(120).optional(),
});
export type RetentionPolicyEffectiveQuery = z.infer<typeof RetentionPolicyEffectiveQuerySchema>;

/**
 * GET /v1/legal-holds — filtres optionnels.
 */
export const LegalHoldFilterSchema = z.object({
  status: LegalHoldStatusSchema.optional(),
  scope_kind: LegalHoldScopeKindSchema.optional(),
  scope_id: UuidSchema.optional(),
  placed_by: UuidSchema.optional(),
});
export type LegalHoldFilter = z.infer<typeof LegalHoldFilterSchema>;

/**
 * GET /v1/legal-holds/active — query strict.
 * Renvoie un boolean (existe-t-il un legal_hold actif couvrant cette ressource ?).
 *
 * IMPORTANT (PO-Q4) : `false` ne prouve PAS l'existence de la ressource.
 * La fonction SQL `app.has_active_legal_hold(...)` retourne aussi `false`
 * pour un scope_id inexistant.
 */
export const LegalHoldActiveQuerySchema = z.object({
  scope_kind: LegalHoldScopeKindSchema,
  scope_id: UuidSchema.optional(),
});
export type LegalHoldActiveQuery = z.infer<typeof LegalHoldActiveQuerySchema>;

/**
 * GET /v1/retention-audit-log — filtres optionnels INDIVIDUELLEMENT mais
 * AU MOINS UN champ doit être fourni (pattern F-01).
 *
 * Refine déléguée à un schema dérivé (cf. `RetentionAuditLogListQuerySchema`)
 * pour la même raison que `DocumentLinkListQuerySchema` : on ne passe pas le
 * schema avec `.refine()` à `@hono/zod-validator` côté API — la règle est
 * appliquée dans le handler via `ApiErrors.badRequest` pour rester aligné
 * avec l'enveloppe d'erreur standard.
 */
export const RetentionAuditLogFilterSchema = z.object({
  action: RetentionActionLogSchema.optional(),
  target_kind: z.string().min(1).max(120).optional(),
  target_id: UuidSchema.optional(),
  policy_id: UuidSchema.optional(),
  legal_hold_id: UuidSchema.optional(),
  /** ISO datetime — borne basse `created_at >= from` (incluse). */
  from: z.string().datetime().optional(),
  /** ISO datetime — borne haute `created_at <= to` (incluse). */
  to: z.string().datetime().optional(),
});
export type RetentionAuditLogFilter = z.infer<typeof RetentionAuditLogFilterSchema>;

export const RetentionAuditLogListQuerySchema = PaginationSchema.merge(
  RetentionAuditLogFilterSchema,
).refine(
  (q) =>
    q.action !== undefined ||
    q.target_kind !== undefined ||
    q.target_id !== undefined ||
    q.policy_id !== undefined ||
    q.legal_hold_id !== undefined ||
    q.from !== undefined ||
    q.to !== undefined,
  {
    // 9.K.13 (D58.1-c refines techniques) — wording EN technique (P-2
    // ADR 0027 fallback dev). User-facing bilingue côté handler API via
    // `bilingualHttpError("RETENTION_AUDIT_LOG_FILTER_REQUIRED", 400)`
    // (déjà câblé 9.K.8 dans `apps/api/src/routes/retention.ts`).
    message:
      "At least one filter is required: action, target_kind, target_id, policy_id, legal_hold_id, from, or to.",
    path: ["_filters"],
  },
);
export type RetentionAuditLogListQuery = z.infer<typeof RetentionAuditLogListQuerySchema>;

// =============================================================================
// Dry-run worker (9.H.4) — schémas metadata audit
// =============================================================================
//
// Ces schémas sont les contrats entre :
//   • producteur : `apps/worker` (job `data-retention` en mode dry-run)
//   • consommateurs : `apps/api` (lecture via `GET /v1/retention-audit-log`)
//                     `apps/web` (futur — affichage humain du plan)
//
// Volontairement RESTREINTS aux deux actions ajoutées par migration 0026 :
// `dry_run_planned` et `dry_run_no_policy`. Les autres actions historiques
// (`anonymized`, `archived`, ...) gardent une `metadata` libre `z.record(...)`.
//
// Pas de garantie 100 % stricte sur `metadata` côté `RetentionAuditLogRowSchema`
// (qui reste `z.record(z.unknown())`) — ces schémas dédiés permettent à un
// consommateur de FAIRE un narrow safe quand il sait qu'il regarde une ligne
// dry-run.
//
// =============================================================================

/** `metadata.actor` — identique pour les deux actions dry-run. */
export const RetentionDryRunActorSchema = z.object({
  kind: z.literal("system"),
  name: z.literal("kollectivia-worker"),
  job: z.literal("data-retention"),
  run_id: z.string().min(1),
  worker_version: z.string().min(1),
});
export type RetentionDryRunActor = z.infer<typeof RetentionDryRunActorSchema>;

/** Un item du plan — détaille une (table, policy effective, compteurs). */
export const RetentionDryRunPlanItemSchema = z.object({
  table_name: z.string(),
  column_name: z.string(),
  data_category: z.string(),
  module: z.string().nullable(),
  policy: z
    .object({
      id: UuidSchema,
      tenant_id: UuidSchema.nullable(),
      retention_days: z.number().int().nullable(),
      legal_minimum_days: z.number().int().nullable(),
      action_on_expiry: z.enum(["anonymize", "archive", "purge", "none"]),
      policy_basis: z.string().nullable(),
    })
    .nullable(),
  candidates_total: z.number().int().nonnegative(),
  candidates_under_legal_hold: z.number().int().nonnegative(),
  candidates_to_act_on: z.number().int().nonnegative(),
  sample_target_ids: z.array(UuidSchema),
  planned_action: z.enum(["anonymize", "archive", "purge", "none"]),
  /** Présent si le scan candidates a été tronqué (cap 10 000 par item). */
  truncated_candidates: z.literal(true).optional(),
});
export type RetentionDryRunPlanItem = z.infer<typeof RetentionDryRunPlanItemSchema>;

/** Totals agrégés du plan tenant. */
export const RetentionDryRunTotalsSchema = z.object({
  items_count: z.number().int().nonnegative(),
  items_without_policy_count: z.number().int().nonnegative(),
  candidates_total: z.number().int().nonnegative(),
  candidates_under_legal_hold: z.number().int().nonnegative(),
  candidates_to_act_on: z.number().int().nonnegative(),
});
export type RetentionDryRunTotals = z.infer<typeof RetentionDryRunTotalsSchema>;

/**
 * `metadata` pour `action='dry_run_planned'`.
 *
 * `truncated=true` ⇒ `items[]` peut avoir été réduit pour rester sous le cap
 * 256 KB ; `items_included` indique le compte effectif après troncation.
 * Les `totals` restent corrects (calculés AVANT troncation).
 */
export const RetentionDryRunPlannedMetadataSchema = z.object({
  actor: RetentionDryRunActorSchema,
  mode: z.literal("dry-run"),
  computed_at: z.string(),
  started_at: z.string(),
  totals: RetentionDryRunTotalsSchema,
  items: z.array(RetentionDryRunPlanItemSchema),
  truncated: z.literal(true).optional(),
  items_included: z.number().int().nonnegative().optional(),
});
export type RetentionDryRunPlannedMetadata = z.infer<typeof RetentionDryRunPlannedMetadataSchema>;

/** `metadata` pour `action='dry_run_no_policy'`. */
export const RetentionDryRunNoPolicyMetadataSchema = z.object({
  actor: RetentionDryRunActorSchema,
  mode: z.literal("dry-run"),
  computed_at: z.string(),
  started_at: z.string(),
  items_without_policy: z.array(
    z.object({
      table_name: z.string(),
      column_name: z.string(),
      data_category: z.string(),
      module: z.string().nullable(),
    }),
  ),
  count: z.number().int().nonnegative(),
});
export type RetentionDryRunNoPolicyMetadata = z.infer<typeof RetentionDryRunNoPolicyMetadataSchema>;
